import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { KeyRound, Check, X, ShieldAlert, ShieldCheck, RefreshCcw, Link as LinkIcon, Send, AlertTriangle, Sparkles, Mail, Wallet, Camera, History, RotateCw, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import { useSectionTheme } from "@/hooks/useSectionTheme";

const STEPS = [
  { key: "preferences_saved_at", label: "Prefs" },
  { key: "id_uploaded_at", label: "ID" },
  { key: "esign_signed_at", label: "Sign" },
  { key: "payment_completed_at", label: "Paid" },
  { key: "deposit_authorized_at", label: "Deposit" },
  { key: "upsells_paid_at", label: "Upsells" },
];

function statusChip(j) {
  if (j.all_complete) return <span className="text-[10px] tracking-widest uppercase text-teal-700 bg-teal-50 border border-teal-100 px-2 py-1 rounded-full">Complete</span>;
  if (j.status === "in_progress") return <span className="text-[10px] tracking-widest uppercase text-amber-700 bg-amber-50 border border-amber-100 px-2 py-1 rounded-full">In progress</span>;
  return <span className="text-[10px] tracking-widest uppercase text-slate-500 bg-slate-50 border border-slate-200 px-2 py-1 rounded-full">Pending</span>;
}

export default function Journeys() {
  const sectionTheme = useSectionTheme("journeys");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [presets, setPresets] = useState([]);
  const [flagTarget, setFlagTarget] = useState(null);
  const [flagReason, setFlagReason] = useState("");
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [captureTarget, setCaptureTarget] = useState(null);
  const [captureForm, setCaptureForm] = useState({ amount: "", reason: "" });
  const [capturePhotos, setCapturePhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [captureStaged, setCaptureStaged] = useState(null); // { pending_captures, pending_total, remaining, deposit_amount }
  const [finalising, setFinalising] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [history, setHistory] = useState(null);
  const [timelineTarget, setTimelineTarget] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [q, setQ] = useState("");
  // Session Z50 — click the "Sign" tick in a row to see who signed, which
  // bespoke agreement version they agreed to, and download the exact PDF.
  const [signInfoTarget, setSignInfoTarget] = useState(null);
  const [downloadingSigned, setDownloadingSigned] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/journeys")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const copyLink = async (j) => {
    const link = `${window.location.origin}/journey/${j.token}`;
    await navigator.clipboard.writeText(link);
    toast.success("Guest check-in link copied");
  };

  // Session Z49 — open the guest portal in a new tab so staff can QA the
  // exact page the guest sees without triggering an email send.
  const previewAsGuest = (j) => {
    if (!j.token) {
      toast.error("No token yet — send the journey first");
      return;
    }
    window.open(`/journey/${j.token}`, "_blank", "noopener");
  };

  const resend = async (j) => {
    try {
      const { data } = await api.post(`/journeys/${j.id}/send`);
      toast.success(`Resent to ${data.sent_to}`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const flagDamage = async () => {
    if (!flagTarget) return;
    try {
      await api.post(`/journeys/${flagTarget.id}/flag-damage`, { reason: flagReason });
      toast.success("Damage flagged — deposit hold will not auto-release");
      setFlagTarget(null); setFlagReason("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const clearDamage = async (j) => {
    try {
      await api.post(`/journeys/${j.id}/clear-damage`);
      toast.success("Damage flag cleared");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const releaseNow = async () => {
    if (!releaseTarget) return;
    try {
      const { data } = await api.post(`/journeys/${releaseTarget.id}/release-deposit`);
      toast.success(data.message || "Deposit released");
      setReleaseTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const loadStaged = async (jid) => {
    try {
      const { data } = await api.get(`/journeys/${jid}/pending-captures`);
      setCaptureStaged(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const openCapture = async (j) => {
    setCaptureTarget(j);
    setCaptureForm({ amount: "", reason: j.damage_reason || "" });
    setCapturePhotos([]);
    setCaptureStaged(null);
    await loadStaged(j.id);
    try {
      const { data } = await api.get("/capture-presets");
      setPresets(data);
    } catch { /* soft-fail */ }
  };

  const applyPreset = async (p) => {
    setCaptureForm({
      amount: p.default_amount ? String(p.default_amount) : "",
      reason: p.reason || "",
    });
    setCapturePhotos(p.photo_urls || []);
    try {
      await api.post(`/capture-presets/${p.id}/use`);
      setPresets((prev) => prev.map((x) =>
        x.id === p.id ? { ...x, usage_count: (x.usage_count || 0) + 1, last_used_at: new Date().toISOString() } : x
      ));
    } catch { /* soft-fail — UX shouldn't block */ }
  };

  const savePreset = async () => {
    const title = (window.prompt("Preset title", captureForm.reason.slice(0, 40) || "Preset") || "").trim();
    if (!title) return;
    try {
      const { data } = await api.post("/capture-presets", {
        title,
        reason: captureForm.reason,
        default_amount: captureForm.amount ? Number(captureForm.amount) : null,
        photo_urls: capturePhotos,
      });
      setPresets((prev) => [data, ...prev]);
      toast.success("Preset saved");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const deletePreset = async (id) => {
    if (!window.confirm("Delete this preset?")) return;
    try {
      await api.delete(`/capture-presets/${id}`);
      setPresets((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const uploadCapturePhoto = async (file) => {
    if (!captureTarget || !file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/journeys/${captureTarget.id}/capture-photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCapturePhotos((p) => [...p, data.url]);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const addCaptureItem = async () => {
    if (!captureTarget) return;
    const amt = Number(captureForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    try {
      await api.post(`/journeys/${captureTarget.id}/pending-captures`, {
        amount: amt, reason: captureForm.reason, photo_urls: capturePhotos,
      });
      setCaptureForm({ amount: "", reason: "" });
      setCapturePhotos([]);
      await loadStaged(captureTarget.id);
      toast.success("Line item added");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeCaptureItem = async (itemId) => {
    if (!captureTarget) return;
    try {
      await api.delete(`/journeys/${captureTarget.id}/pending-captures/${itemId}`);
      await loadStaged(captureTarget.id);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const finaliseCapture = async () => {
    if (!captureTarget) return;
    setFinalising(true);
    try {
      const { data } = await api.post(`/journeys/${captureTarget.id}/finalise-capture`);
      toast.success(`£${data.captured.toFixed(2)} captured from the deposit hold (${data.items} line item${data.items === 1 ? "" : "s"})`);
      setCaptureTarget(null);
      setCaptureStaged(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setFinalising(false);
    }
  };

  const openHistory = async (j) => {
    setHistoryTarget(j);
    setHistory(null);
    try {
      const { data } = await api.get(`/journeys/${j.id}/captures`);
      setHistory(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const resendEmail = async (e) => {
    setResendingId(e.id);
    try {
      const { data } = await api.post(`/journeys/${timelineTarget.id}/emails/${e.id}/resend`);
      if (data.ok) toast.success("Email resent");
      else toast.error("Resend failed — Resend API returned an error");
      const { data: fresh } = await api.get(`/journeys/${timelineTarget.id}/emails`);
      setTimeline(fresh);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setResendingId(null);
    }
  };

  const openTimeline = async (j) => {
    setTimelineTarget(j);
    setTimeline([]);
    try {
      const { data } = await api.get(`/journeys/${j.id}/emails`);
      setTimeline(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  // Session Z50 — stream the snapshotted agreement PDF through the
  // authenticated axios instance so admin role is enforced, then push a
  // blob download. Falls back cleanly when no PDF was attached at sign time.
  const downloadSignedAgreement = async (j) => {
    setDownloadingSigned(true);
    try {
      const res = await api.get(`/journeys/${j.id}/signed-agreement`, {
        responseType: "blob",
      });
      const snap = j.signed_agreement_snapshot || {};
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = snap.original_filename || "signed-agreement.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err?.response?.data instanceof Blob
        ? "Could not download the signed agreement"
        : apiError(err, "Download failed");
      toast.error(msg);
    } finally {
      setDownloadingSigned(false);
    }
  };

  const filtered = rows.filter((r) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (r.guest_name || "").toLowerCase().includes(s)
      || (r.guest_email || "").toLowerCase().includes(s)
      || (r.property_name || "").toLowerCase().includes(s);
  });

  const summary = {
    total: rows.length,
    completed: rows.filter((r) => r.all_complete).length,
    in_progress: rows.filter((r) => r.status === "in_progress" && !r.all_complete).length,
    damage: rows.filter((r) => r.damage_flagged).length,
  };

  return (
    <Layout title="Guest journeys" subtitle="Every check-in in one glance — nothing falls through the cracks.">
      <div data-testid="journeys-root" className="space-y-6">
        <section className={`ns-section ns-section-${sectionTheme}`} data-section-theme={sectionTheme}>
          <div className="flex items-start gap-3 mb-4">
            <div className={`ns-rail ns-rail-${sectionTheme} self-stretch min-h-[2.5rem]`} />
            <div className="flex-1 min-w-0">
              <div className="ns-label text-violet-800">Journey pulse</div>
              <div className="font-serif text-lg text-slate-900 mt-0.5">Where every check-in stands</div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Journeys", value: summary.total, icon: <KeyRound className="w-4 h-4" /> },
              { label: "Completed", value: summary.completed, icon: <ShieldCheck className="w-4 h-4" />, tone: "teal" },
              { label: "In progress", value: summary.in_progress, icon: <Sparkles className="w-4 h-4" />, tone: "amber" },
              { label: "Damage flagged", value: summary.damage, icon: <ShieldAlert className="w-4 h-4" />, tone: "red" },
            ].map((s) => {
              const toneBg = s.tone === "teal" ? "bg-teal-50 text-teal-700 border-teal-100"
                : s.tone === "amber" ? "bg-amber-50 text-amber-700 border-amber-100"
                : s.tone === "red" ? "bg-red-50 text-red-700 border-red-100"
                : "bg-slate-50 text-slate-700 border-slate-200";
              return (
                <div key={s.label} className="ns-card p-5 relative overflow-hidden">
                  <div className={`absolute inset-x-0 top-0 h-1 ${s.tone === "teal" ? "bg-teal-500" : s.tone === "amber" ? "bg-amber-500" : s.tone === "red" ? "bg-red-500" : "bg-slate-400"}`} />
                  <div className={`ns-label inline-flex items-center gap-2 rounded-full border px-2.5 py-1 ${toneBg}`}>
                    {s.icon}{s.label}
                  </div>
                  <div className="font-serif text-3xl mt-3 text-slate-900">{s.value}</div>
                </div>
              );
            })}
          </div>
        </section>

        <div className="flex items-center gap-3">
          <Input
            data-testid="journeys-search"
            placeholder="Search guest / property / email…"
            className="max-w-xs h-11 bg-white"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button variant="outline" onClick={load} className="rounded-full h-11 px-5">
            <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        <div className="ns-card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="text-left ns-label px-6 py-4">Guest</th>
                <th className="text-left ns-label px-4 py-4">Property</th>
                <th className="text-left ns-label px-4 py-4">Check-in</th>
                <th className="ns-label px-4 py-4">Status</th>
                {STEPS.map((s) => (
                  <th key={s.key} className="ns-label px-2 py-4 text-center">{s.label}</th>
                ))}
                <th className="ns-label px-4 py-4">Deposit</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="text-center py-12 text-slate-400 italic">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={11} className="text-center py-12 text-slate-400 italic">No guest journeys yet.</td></tr>
              )}
              {filtered.map((j) => (
                <tr key={j.id} data-testid={`journey-row-${j.id}`} className="border-t border-slate-100 hover:bg-teal-50/20">
                  <td className="px-6 py-4">
                    <div className="font-medium">{j.guest_name || "—"}</div>
                    <div className="text-xs text-slate-500">{j.guest_email}</div>
                  </td>
                  <td className="px-4 py-4 text-slate-600">{j.property_name}</td>
                  <td className="px-4 py-4 text-slate-600">{j.check_in}</td>
                  <td className="px-4 py-4">{statusChip(j)}</td>
                  {STEPS.map((s) => {
                    const done = !!j[s.key];
                    const req =
                      (s.key === "id_uploaded_at" && j.require_id) ||
                      (s.key === "esign_signed_at" && j.require_esign) ||
                      (s.key === "payment_completed_at" && j.require_balance) ||
                      (s.key === "deposit_authorized_at" && j.require_deposit) ||
                      (s.key === "preferences_saved_at") ||
                      (s.key === "upsells_paid_at" && (j.purchased_upsells || []).length > 0);
                    // Session Z50 — the Sign tick is clickable when signed, opens a dialog
                    // with the stamped agreement version + download link.
                    if (s.key === "esign_signed_at" && done) {
                      return (
                        <td key={s.key} className="px-2 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => setSignInfoTarget(j)}
                            data-testid={`journey-sign-info-${j.id}`}
                            title={`Signed by ${j.signed_name || "guest"} · click for details`}
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full hover:bg-teal-50"
                          >
                            <Check className="w-4 h-4 text-teal-600" />
                          </button>
                        </td>
                      );
                    }
                    return (
                      <td key={s.key} className="px-2 py-4 text-center">
                        {done ? (
                          <Check className="w-4 h-4 text-teal-600 inline" />
                        ) : req ? (
                          <X className="w-4 h-4 text-slate-300 inline" />
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-4">
                    {j.deposit_captured_at ? (
                      <button
                        onClick={() => openHistory(j)}
                        data-testid={`journey-history-${j.id}`}
                        className="text-[10px] tracking-widest uppercase text-slate-600 bg-slate-50 border border-slate-200 px-2 py-1 rounded-full hover:bg-slate-100"
                        title={`£${Number(j.deposit_captured_amount || 0).toFixed(2)} captured · click for history`}
                      >
                        <History className="w-3 h-3 inline mr-1" /> £{Number(j.deposit_captured_amount || 0).toFixed(2)}
                      </button>
                    ) : j.damage_flagged ? (
                      <button
                        onClick={() => openCapture(j)}
                        data-testid={`journey-capture-${j.id}`}
                        className="text-[10px] tracking-widest uppercase text-red-700 bg-red-50 border border-red-100 px-2 py-1 rounded-full hover:bg-red-100"
                        title={j.damage_reason || "Flagged — click to capture"}
                      >
                        <Wallet className="w-3 h-3 inline mr-1" /> Capture
                      </button>
                    ) : j.deposit_released_at ? (
                      <span className="text-[10px] tracking-widest uppercase text-slate-500">Released</span>
                    ) : j.deposit_authorized_at ? (
                      <span className="text-[10px] tracking-widest uppercase text-teal-700">Held</span>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <button
                      data-testid={`journey-timeline-${j.id}`}
                      onClick={() => openTimeline(j)}
                      className="text-slate-300 hover:text-teal-700 p-1.5"
                      title="Email timeline"
                    >
                      <Mail className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`journey-copy-${j.id}`}
                      onClick={() => copyLink(j)}
                      className="text-slate-300 hover:text-teal-700 p-1.5"
                      title="Copy check-in link"
                    >
                      <LinkIcon className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`journey-preview-${j.id}`}
                      onClick={() => previewAsGuest(j)}
                      className="text-slate-300 hover:text-teal-700 p-1.5"
                      title="Preview as guest (opens the portal in a new tab, no email)"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => resend(j)}
                      className="text-slate-300 hover:text-teal-700 p-1.5"
                      title="Resend email"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                    {j.deposit_authorized_at && !j.deposit_released_at && !j.damage_flagged && (
                      <button
                        onClick={() => setReleaseTarget(j)}
                        className="text-slate-300 hover:text-teal-700 p-1.5"
                        title="Release deposit hold now"
                      >
                        <ShieldCheck className="w-4 h-4" />
                      </button>
                    )}
                    {j.deposit_authorized_at && !j.deposit_released_at && !j.damage_flagged && (
                      <button
                        data-testid={`journey-flag-${j.id}`}
                        onClick={() => setFlagTarget(j)}
                        className="text-slate-300 hover:text-red-600 p-1.5"
                        title="Flag damage — prevents auto-release"
                      >
                        <ShieldAlert className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!flagTarget} onOpenChange={(v) => !v && setFlagTarget(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Flag damage</DialogTitle></DialogHeader>
          {flagTarget && (
            <div className="space-y-4 text-sm">
              <p className="text-slate-500">
                Flagging the stay at <span className="font-medium text-slate-800">{flagTarget.property_name}</span>
                {" "}for <span className="font-medium">{flagTarget.guest_name}</span> will prevent the deposit hold
                being auto-released. You can still capture or release the hold from Stripe.
              </p>
              <div>
                <Label className="ns-label">Reason (optional)</Label>
                <Textarea
                  data-testid="journey-flag-reason"
                  rows={3}
                  className="mt-2 bg-white"
                  value={flagReason}
                  onChange={(e) => setFlagReason(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFlagTarget(null)}>Cancel</Button>
            <Button data-testid="journey-flag-confirm" onClick={flagDamage} className="rounded-full ns-btn-primary px-6">Flag damage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!releaseTarget} onOpenChange={(v) => !v && setReleaseTarget(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Release deposit hold</DialogTitle></DialogHeader>
          {releaseTarget && (
            <p className="text-sm text-slate-500">
              Release the £{Number(releaseTarget.deposit_amount || 0).toFixed(2)} authorised hold for {releaseTarget.guest_name}?
              The funds will unwind on their card within 5–10 working days.
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReleaseTarget(null)}>Cancel</Button>
            <Button onClick={releaseNow} className="rounded-full ns-btn-primary px-6">Release now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!captureTarget} onOpenChange={(v) => !v && setCaptureTarget(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Capture from deposit</DialogTitle></DialogHeader>
          {captureTarget && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <div className="ns-card p-3">
                  <div className="ns-label text-slate-500">Hold</div>
                  <div className="font-serif text-xl mt-1">£{Number(captureStaged?.deposit_amount || captureTarget.deposit_amount || 0).toFixed(2)}</div>
                </div>
                <div className="ns-card p-3">
                  <div className="ns-label text-amber-700">Staged</div>
                  <div className="font-serif text-xl mt-1 text-amber-700">£{Number(captureStaged?.pending_total || 0).toFixed(2)}</div>
                </div>
                <div className="ns-card p-3">
                  <div className="ns-label text-teal-700">Remaining</div>
                  <div className="font-serif text-xl mt-1 text-teal-700">£{Number(captureStaged?.remaining || captureTarget.deposit_amount || 0).toFixed(2)}</div>
                </div>
              </div>

              {(captureStaged?.pending_captures || []).length > 0 && (
                <div className="border border-amber-100 rounded-xl overflow-hidden bg-amber-50/30" data-testid="capture-staged-list">
                  {(captureStaged.pending_captures || []).map((p) => (
                    <div key={p.id} data-testid={`capture-staged-${p.id}`} className="p-3 border-t border-amber-100/60 first:border-t-0 flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-900">£{Number(p.amount).toFixed(2)}</div>
                        {p.reason && <div className="text-xs text-slate-600 mt-0.5">{p.reason}</div>}
                        {(p.photo_urls || []).length > 0 && (
                          <div className="text-[10px] text-slate-500 mt-0.5">{(p.photo_urls || []).length} photo{(p.photo_urls || []).length === 1 ? "" : "s"} attached</div>
                        )}
                      </div>
                      <button
                        data-testid={`capture-staged-remove-${p.id}`}
                        onClick={() => removeCaptureItem(p.id)}
                        className="shrink-0 text-red-600 hover:text-red-700 text-xs"
                        title="Remove line item"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="border border-slate-100 rounded-xl p-4">
                <div className="ns-label mb-3">Add another line item</div>
                {presets.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-1.5 items-center" data-testid="capture-presets-strip">
                    <span className="text-[10px] tracking-widest uppercase text-slate-400 mr-1">Presets</span>
                    {[...presets].sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0)).map((p) => (
                      <button
                        key={p.id}
                        data-testid={`capture-preset-${p.id}`}
                        onClick={() => applyPreset(p)}
                        className="group inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-[11px] bg-teal-50 text-teal-800 hover:bg-teal-100 border border-teal-100"
                        title={`${p.reason}${p.usage_count ? ` · used ${p.usage_count}×` : " · never used"}`}
                      >
                        {p.title}
                        {p.default_amount != null && (
                          <span className="text-teal-600">·&nbsp;£{Number(p.default_amount).toFixed(0)}</span>
                        )}
                        {p.usage_count > 0 && (
                          <span className="text-[9px] text-teal-600/70 ml-0.5" data-testid={`capture-preset-usage-${p.id}`}>{p.usage_count}×</span>
                        )}
                        <span
                          onClick={(e) => { e.stopPropagation(); deletePreset(p.id); }}
                          className="ml-1 w-4 h-4 rounded-full text-teal-500/70 hover:text-red-600 hover:bg-white flex items-center justify-center"
                          title="Delete preset"
                          role="button"
                        >
                          <X className="w-2.5 h-2.5" />
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="ns-label text-slate-500">Amount (£)</Label>
                    <Input
                      data-testid="journey-capture-amount"
                      type="number" step="0.01" min={0} max={Number(captureStaged?.remaining || captureTarget.deposit_amount || 0)}
                      className="mt-2 h-11 bg-white"
                      value={captureForm.amount}
                      onChange={(e) => setCaptureForm({ ...captureForm, amount: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="ns-label text-slate-500">Reason</Label>
                    <Input
                      className="mt-2 h-11 bg-white"
                      placeholder="Replacement lamp"
                      value={captureForm.reason}
                      onChange={(e) => setCaptureForm({ ...captureForm, reason: e.target.value })}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="ns-label text-slate-500">Evidence photos (optional)</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {capturePhotos.map((url, i) => (
                      <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200">
                        <img src={url} alt="evidence" className="w-full h-full object-cover" />
                        <button
                          onClick={() => setCapturePhotos((p) => p.filter((_, idx) => idx !== i))}
                          className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white/90 text-red-600 flex items-center justify-center"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <label
                      data-testid="journey-capture-photo-btn"
                      className={`w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-teal-500 hover:text-teal-700 cursor-pointer ${uploadingPhoto ? "opacity-50 pointer-events-none" : ""}`}
                    >
                      <Camera className="w-4 h-4" />
                      <span className="text-[9px] mt-0.5">{uploadingPhoto ? "…" : "Photo"}</span>
                      <input
                        type="file" accept="image/*" className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) uploadCapturePhoto(f);
                          e.target.value = "";
                        }}
                      />
                    </label>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <Button
                    data-testid="journey-capture-add-item"
                    onClick={addCaptureItem}
                    variant="outline"
                    className="rounded-full h-10 px-5 text-xs"
                    disabled={!captureForm.amount}
                  >
                    Add line item
                  </Button>
                  <button
                    data-testid="journey-capture-save-preset"
                    onClick={savePreset}
                    disabled={!captureForm.reason && !captureForm.amount}
                    className="text-[11px] tracking-widest uppercase text-slate-500 hover:text-teal-700 disabled:opacity-40"
                    title="Save this reason & amount as a reusable preset"
                  >
                    Save as preset →
                  </button>
                </div>
              </div>

              <div className="text-[11px] text-slate-400">
                Stage as many partial captures as you need; when you finalise, Stripe charges the total once and releases the rest back to the guest&apos;s card.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCaptureTarget(null)}>Close</Button>
            <Button
              data-testid="journey-capture-confirm"
              onClick={finaliseCapture}
              disabled={finalising || !(captureStaged?.pending_total > 0)}
              className="rounded-full ns-btn-primary px-6"
            >
              {finalising ? "Capturing…" : `Finalise £${Number(captureStaged?.pending_total || 0).toFixed(2)} capture`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyTarget} onOpenChange={(v) => { if (!v) { setHistoryTarget(null); setHistory(null); } }}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Capture history</DialogTitle></DialogHeader>
          {historyTarget && (
            <div className="space-y-4 text-sm">
              <div className="text-slate-500">
                Audit trail of every capture from {historyTarget.guest_name}&apos;s £{Number(historyTarget.deposit_amount || 0).toFixed(2)} deposit hold.
              </div>
              {!history ? (
                <div className="text-center py-8 text-slate-400 italic">Loading…</div>
              ) : history.captures.length === 0 ? (
                <div className="text-center py-8 text-slate-400 italic">No captures recorded.</div>
              ) : (
                <div className="border border-slate-100 rounded-xl overflow-hidden">
                  {history.captures.map((cap) => (
                    <div key={cap.id} data-testid={`capture-entry-${cap.id}`} className="p-4 border-t border-slate-100 first:border-t-0">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-serif text-xl text-slate-900">£{Number(cap.amount).toFixed(2)}</div>
                        <div className="text-[11px] text-slate-400">
                          {new Date(cap.captured_at).toLocaleString("en-GB")}
                        </div>
                      </div>
                      {cap.reason && (
                        <div className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{cap.reason}</div>
                      )}
                      <div className="text-[11px] text-slate-400 mt-2">
                        Captured by {cap.captured_by || "—"}
                        {cap.stripe_reference && ` · Stripe ${String(cap.stripe_reference).slice(0, 18)}…`}
                      </div>
                      {(cap.photo_urls || []).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {cap.photo_urls.map((url, i) => (
                            <a key={i} href={url} target="_blank" rel="noreferrer" className="block w-16 h-16 rounded-lg overflow-hidden border border-slate-200 hover:ring-2 hover:ring-teal-500">
                              <img src={url} alt="evidence" className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { setHistoryTarget(null); setHistory(null); }} className="rounded-full ns-btn-primary px-6">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!timelineTarget} onOpenChange={(v) => !v && setTimelineTarget(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Email timeline</DialogTitle></DialogHeader>
          {timelineTarget && (
            <div className="space-y-4 text-sm">
              <div className="text-slate-500">
                Every email we&apos;ve sent for {timelineTarget.guest_name}&apos;s check-in.
              </div>
              {timeline.length === 0 ? (
                <div className="text-center py-8 text-slate-400 italic">No emails logged yet.</div>
              ) : (
                <div className="border border-slate-100 rounded-xl overflow-hidden">
                  {timeline.map((e) => (
                    <div key={e.id} data-testid={`journey-email-${e.id}`} className="p-4 border-t border-slate-100 first:border-t-0 flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${e.ok ? "bg-teal-50 text-teal-700" : "bg-red-50 text-red-600"}`}>
                        <Mail className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-sm truncate">{e.subject}</div>
                          <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full ${e.ok ? "text-teal-700 bg-teal-50" : "text-red-600 bg-red-50"}`}>
                            {e.kind.replace(/_/g, " ")}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">to {e.to}</div>
                        <div className="text-[11px] text-slate-400 mt-1">
                          {new Date(e.sent_at).toLocaleString("en-GB")} · {e.ok ? "delivered" : "failed"}
                          {e.email_reference && ` · ${e.email_reference.slice(0, 12)}…`}
                        </div>
                      </div>
                      <button
                        data-testid={`journey-email-resend-${e.id}`}
                        onClick={() => resendEmail(e)}
                        disabled={resendingId === e.id}
                        className={`shrink-0 text-[10px] tracking-widest uppercase px-2 py-1 rounded-full border transition ${
                          e.ok
                            ? "border-slate-200 text-slate-500 hover:bg-slate-50"
                            : "border-red-200 text-red-700 bg-red-50 hover:bg-red-100"
                        } ${resendingId === e.id ? "opacity-50" : ""}`}
                        title={e.ok ? "Resend this email" : "Retry — original send failed"}
                      >
                        <RotateCw className={`w-3 h-3 inline mr-1 ${resendingId === e.id ? "animate-spin" : ""}`} />
                        {e.ok ? "Resend" : "Retry"}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setTimelineTarget(null)} className="rounded-full ns-btn-primary px-6">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!signInfoTarget} onOpenChange={(v) => !v && setSignInfoTarget(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="journey-sign-info-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Signed agreement</DialogTitle>
          </DialogHeader>
          {signInfoTarget && (() => {
            const snap = signInfoTarget.signed_agreement_snapshot || null;
            return (
              <div className="space-y-5 text-sm">
                <div className="rounded-xl border border-slate-100 bg-stone-50/60 p-4">
                  <div className="ns-label text-teal-700">Audit</div>
                  <div className="font-serif text-xl text-slate-900 mt-1">
                    Signed by {signInfoTarget.signed_name || "—"}
                  </div>
                  <div className="text-xs text-slate-500 mt-2 space-y-0.5">
                    <div>
                      Signed at:{" "}
                      {signInfoTarget.esign_signed_at
                        ? new Date(signInfoTarget.esign_signed_at).toLocaleString("en-GB")
                        : "—"}
                    </div>
                    {signInfoTarget.esign_ip && (
                      <div>IP address: <span className="font-mono">{signInfoTarget.esign_ip}</span></div>
                    )}
                    <div>Journey ID: <span className="font-mono text-[11px]">{signInfoTarget.id}</span></div>
                  </div>
                </div>
                {snap ? (
                  <div className="rounded-xl border border-slate-100 p-4">
                    <div className="ns-label text-teal-700">Bespoke rental agreement</div>
                    <div className="mt-2 text-slate-700 truncate" title={snap.original_filename}>
                      {snap.original_filename}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">
                      Version {snap.version} · uploaded{" "}
                      {snap.uploaded_at
                        ? new Date(snap.uploaded_at).toLocaleDateString("en-GB")
                        : "—"}
                    </div>
                    <Button
                      data-testid="journey-signed-agreement-download"
                      onClick={() => downloadSignedAgreement(signInfoTarget)}
                      disabled={downloadingSigned}
                      className="mt-4 rounded-full ns-btn-primary px-5 inline-flex items-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      {downloadingSigned ? "Downloading…" : "Download signed PDF"}
                    </Button>
                    <div className="text-[11px] text-slate-400 mt-2">
                      This is the exact PDF the guest saw when they tapped Agree &amp; sign — any later edit of the property's agreement doesn't change this record.
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-amber-100 bg-amber-50/60 p-4 text-slate-700">
                    <div className="ns-label text-amber-700">No PDF attached</div>
                    <div className="mt-2 text-sm">
                      This journey was signed against the plain-text house rules — no bespoke PDF was uploaded for the property at the time of signing. Upload a rental agreement PDF on the property page to stamp future signings.
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button
              onClick={() => setSignInfoTarget(null)}
              className="rounded-full ns-btn-primary px-6"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}